import { Component, input, output } from '@angular/core';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { AlertCardComponent } from 'src/app/shared/ui-kit/card/alert-card/alert-card.component';

/** The card's tint, which also decides its icon — a subset of `AlertTone`. */
export type NoticeCardTone =
  | 'warning'
  | 'success'
  | 'error'
  | 'neutral'
  | 'plain';

/**
 * A one-line notice with an optional pill, a supporting line and one action:
 * "something about this record, and what you can do about it".
 *
 * The structured face of `app-alert-card` in its subtle appearance: the tint,
 * icon and layout are the alert card's; this only names what goes in it.
 * Knows nothing about what it announces — the host maps its state onto a
 * tone, a title and, when there is something to do, an action label; no
 * label, no button, which is how a read-only state renders. Every optional
 * part takes `null` or `undefined` alike, so a host binds its own optional
 * fields straight through.
 */
@Component({
  selector: 'app-notice-card',
  standalone: true,
  imports: [AlertCardComponent, NgbTooltip],
  templateUrl: './notice-card.component.html',
  styleUrl: './notice-card.component.css',
  host: { '[attr.data-tone]': 'tone()' },
})
export class NoticeCardComponent {
  readonly tone = input<NoticeCardTone>('plain');

  readonly title = input.required<string>();

  /** A short status after the title, drawn as an `lty-badge`. */
  readonly pill = input<string | null>();

  /** The `lty-badge` modifier of the pill (`success`, `warning`, `gray`…). */
  readonly pillClass = input<string>();

  readonly description = input<string | null>();

  /** The button's label; absent, there is no button. */
  readonly actionLabel = input<string | null>();

  readonly actionStyle = input<'outline' | 'primary'>('outline');

  readonly actionDisabled = input(false);

  /** Shown on hovering the button; typically why it is disabled. */
  readonly actionTooltip = input<string | null>();

  readonly action = output<void>();
}
